import axios from 'axios'
import * as Constants from '../constants'
import { TokenT } from '../types/tokenT'
import { store } from '../app/store'
import { setIsLoggedIn } from '../pages/login/slice'

// Time buffer before expiration to proactively refresh (5 minutes)
const PROACTIVE_REFRESH_BUFFER = 5 * 60 * 1000

export default class Token {
  private static refreshPromise: Promise<TokenT | null> | null = null

  static async getValue(): Promise<string | null> {
    const stringToken = localStorage.getItem(Constants.GTOB_TOKEN_IDENTIFIER)
    if (stringToken) {
      const tokenParsed = JSON.parse(stringToken) as TokenT
      if (Token.isValid(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)) {
        return tokenParsed.access_token
      }

      const newToken = await Token.refresh(tokenParsed, true)
      return newToken ? newToken.access_token : null
    }
    return null
  }

  static isValid(expirationDateIdentifier: string | undefined): boolean {
    const expirationDate = localStorage.getItem(expirationDateIdentifier ?? '')
    return expirationDate ? Number(expirationDate) > new Date().getTime() : false
  }

  /**
   * Check if the token is expiring soon
   */
  static isExpiringSoon(): boolean {
    const expirationDate = localStorage.getItem(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)
    if (!expirationDate) return true
    return Number(expirationDate) - new Date().getTime() < PROACTIVE_REFRESH_BUFFER
  }

  /**
   * Proactively refresh the token if it's expiring soon.
   */
  static async ensureValidToken(): Promise<boolean> {
    const stringToken = localStorage.getItem(Constants.GTOB_TOKEN_IDENTIFIER)
    if (!stringToken) return false

    if (Token.isValid(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER) && !Token.isExpiringSoon()) {
      return true
    }

    if (!Token.isValid(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)) {
      return false
    }

    const tokenParsed = JSON.parse(stringToken) as TokenT
    const newToken = await Token.refresh(tokenParsed, false)
    return newToken !== null
  }

  /**
   * Attempt to refresh the token.
   */
  static async silentRefresh(): Promise<string | null> {
    const stringToken = localStorage.getItem(Constants.GTOB_TOKEN_IDENTIFIER)
    if (!stringToken) return null

    const tokenParsed = JSON.parse(stringToken) as TokenT
    const newToken = await Token.refresh(tokenParsed, false)
    return newToken ? newToken.access_token : null
  }

  /**
   * Clear all token data and dispatch logout
   */
  static clearAndLogout(): void {
    localStorage.removeItem(Constants.GTOB_TOKEN_IDENTIFIER)
    localStorage.removeItem(Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER)
    localStorage.removeItem(Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER)
    store.dispatch(setIsLoggedIn(false))
  }

  private static async refresh(token: TokenT, dispatchLogoutOnFailure: boolean) {
    if (Token.refreshPromise) {
      return Token.refreshPromise
    }

    Token.refreshPromise = Token.performRefresh(token, dispatchLogoutOnFailure)

    try {
      return await Token.refreshPromise
    } finally {
      Token.refreshPromise = null
    }
  }

  private static async performRefresh(
    token: TokenT,
    dispatchLogoutOnFailure: boolean
  ): Promise<TokenT | null> {
    try {
      const requestData = new URLSearchParams()
      requestData.append('grant_type', 'refresh_token')
      requestData.append('client_id', process.env.REACT_APP_OAUTH_CLIENT_ID ?? '')
      requestData.append('refresh_token', token.refresh_token)
      const newToken = await axios<TokenT>({
        url: `${process.env.REACT_APP_API_URL ?? ''}/oauth/token`,
        method: 'post',
        data: requestData,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
        },
      })

      localStorage.setItem(
        Constants.GTOB_TOKEN_EXPIRATION_IDENTIFIER,
        (new Date().getTime() + newToken.data.expires_in * 1000).toString()
      )
      localStorage.setItem(
        Constants.GTOB_TOKEN_LONG_EXPIRATION_IDENTIFIER,
        (new Date().getTime() + Constants.LONG_EXPIRATION_TIME * 1000).toString()
      )
      localStorage.setItem(Constants.GTOB_TOKEN_IDENTIFIER, JSON.stringify(newToken.data))
      return newToken.data
    } catch (error) {
      if (dispatchLogoutOnFailure) {
        Token.clearAndLogout()
      }
      return null
    }
  }
}
